import { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = 'https://didrouteapi.com/api';

// Copy RecordingCell component exactly as it is in ReportTable.js
const RecordingCell = ({ callId, recordingFilename }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const getSimpleFilename = (fullPath) => {
    return fullPath.split('/').pop();
  };

  const handlePlay = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const simpleFilename = getSimpleFilename(recordingFilename);
      const token = localStorage.getItem('buyerToken'); // Changed from 'token' to 'buyerToken'
      const audioUrl = `${API_URL}/recordings/${simpleFilename}`;

      const response = await fetch(audioUrl, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to load audio file: ${response.statusText}`);
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      setAudioUrl(url);
      setIsPlaying(true);
      setLoading(false);
    } catch (err) {
      setError('Failed to load recording');
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      setLoading(true);
      setError(null);

      const simpleFilename = getSimpleFilename(recordingFilename);
      const token = localStorage.getItem('buyerToken'); // Changed from 'token' to 'buyerToken'
      
      const response = await axios({
        url: `${API_URL}/recordings/${simpleFilename}`,
        method: 'GET',
        responseType: 'blob',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', simpleFilename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setLoading(false);
    } catch (err) {
      setError('Failed to download recording');
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center space-x-2">
      {loading ? (
        <span className="text-gray-500">Loading...</span>
      ) : recordingFilename ? (
        <>
          {audioUrl && (
            <div className="flex items-center">
              <audio
                controls
                className="w-32 h-8"
                onEnded={() => {
                  setIsPlaying(false);
                  setAudioUrl(null);
                }}
                onError={() => {
                  setError('Error playing audio');
                  setIsPlaying(false);
                  setAudioUrl(null);
                }}
              >
                <source src={audioUrl} type="audio/mpeg" />
                Your browser does not support the audio element.
              </audio>
              <button
                onClick={() => {
                  setIsPlaying(false);
                  setAudioUrl(null);
                }}
                className="ml-2 text-gray-500 hover:text-gray-700"
                title="Stop"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8 7a1 1 0 00-1 1v4a1 1 0 002 0V8a1 1 0 00-1-1zm4 0a1 1 0 00-1 1v4a1 1 0 002 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          )}
          
          {!isPlaying && (
            <button
              onClick={handlePlay}
              className="text-blue-600 hover:text-blue-800"
              title="Play Recording"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
              </svg>
            </button>
          )}
          
          <button
            onClick={handleDownload}
            className="text-blue-600 hover:text-blue-800"
            title="Download Recording"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
          
          {error && <span className="text-red-500 text-xs">{error}</span>}
        </>
      ) : (
        <span className="text-gray-500">N/A</span>
      )}
    </div>
  );
};

function BuyersReportTable() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    did: '',
    disposition: '',
    campaignName: '',
    targetNumber: '',
    callerNumber: ''
  });

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const applyFilters = () => {
    setPage(1);
    fetchReports();
  };

  const resetFilters = () => {
    setFilters({
      startDate: '',
      endDate: '',
      did: '',
      disposition: '',
      campaignName: '',
      targetNumber: '',
      callerNumber: ''
    });
    setPage(1);
    fetchReports();
  };

  const fetchReports = async () => {
    try {
      const token = localStorage.getItem('buyerToken');
      
      if (!token) {
        setError('No authentication token found. Please login again.');
        return;
      }

      // Create filter parameters
      const filterParams = {
        page,
        limit: 10,
      };

      // Only add non-empty filter values
      if (filters.startDate) {
        filterParams.startDate = filters.startDate;
      }
      if (filters.endDate) {
        filterParams.endDate = filters.endDate;
      }
      if (filters.did) {
        filterParams.did = filters.did;
      }
      if (filters.disposition) {
        filterParams.disposition = filters.disposition;
      }
      if (filters.targetNumber) {
        filterParams.targetNumber = filters.targetNumber;
      }
      if (filters.callerNumber) {
        filterParams.callerNumber = filters.callerNumber;
      }
      
      const response = await axios.get(`${API_URL}/acc/buyer`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: filterParams,
      });

      if (response.data.success) {
        const transformedData = response.data.data.records.map(record => ({
          id: String(record.id || ""),
          callDate: record.created
            ? record.created.replace("T", " ").replace(/\.\d+Z$/, "")
            : "N/A",
          did: String(record.DID_Number || ""),
          disposition: record.sip_code
            ? record.sip_code === "200"
              ? "Answered"
              : record.sip_code === "487"
              ? "Missed Call"
              : "Failed"
            : "N/A",
          terminationReason: record.sip_reason || "N/A",
          campaignName: record.Campaign_Name || "N/A",  // Using Campaign_Name from acc table
          totalTime: `${((record.setuptime || 0) + (record.Billsec || 0))}s`,
          talkTime: typeof record.Billsec === "number" ? `${record.Billsec}s` : "0",
          ringTime: typeof record.setuptime === "number" ? `${record.setuptime}s` : "0",
          callerNumber: record.Caller || "N/A",
          targetName: record.target_name || "N/A",
          targetNumber: record.Forwarded_DID || "N/A",  // Changed to use Forwarded_DID
          recording: (
            <RecordingCell
              callId={record.callid}
              recordingFilename={record.recording_filename || null}
            />
          ),
        }));

        setReports(transformedData);
        setTotalPages(Math.ceil(response.data.data.total / 10));
        setLoading(false);
      } else {
        throw new Error(response.data.error || 'Failed to fetch records');
      }
    } catch (err) {
      setLoading(false);
      setError(err.response?.data?.error || 'Failed to fetch reports');
    }
  };

  useEffect(() => {
    fetchReports();
  }, [page, filters]); // Add filters to dependency array

  return (
    <div className="bg-white rounded-lg mt-2 p-4">
      {/* Filter Toggle Button */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
          Call Reports
        </h2>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-md hover:from-blue-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 text-sm font-medium flex items-center shadow-md"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 mr-2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
            />
          </svg>
          {showFilters ? "Hide Filters" : "Advanced Filters"}
        </button>
      </div>

      {/* Filter Section */}
      <div className={`transition-all duration-300 ease-in-out overflow-hidden ${
        showFilters ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
      }`}>
        <div className="mt-4 bg-white p-3 rounded-lg shadow-md border border-gray-400">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="relative">
              <label htmlFor="did" className="block text-xs font-medium text-gray-700 mb-1">
                DID
              </label>
              <input
                type="text"
                id="did"
                name="did"
                placeholder="Search by DID"
                value={filters.did}
                onChange={handleFilterChange}
                className="pl-3 px-3 py-2 mt-1 block w-full rounded-md bg-gray-50 border border-gray-300 focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
            </div>

            <div className="relative">
              <label htmlFor="disposition" className="block text-xs font-medium text-gray-700 mb-1">
                Disposition
              </label>
              <select
                id="disposition"
                name="disposition"
                value={filters.disposition}
                onChange={handleFilterChange}
                className="pl-3 px-3 py-2 mt-1 block w-full rounded-md bg-gray-50 border border-gray-300 focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              >
                <option value="">All</option>
                <option value="Answered">Answered</option>
                <option value="Missed Call">Missed Call</option>
                <option value="Failed">Failed</option>
              </select>
            </div>

            <div className="relative">
              <label htmlFor="callerNumber" className="block text-xs font-medium text-gray-700 mb-1">
                Caller Number
              </label>
              <input
                type="text"
                id="callerNumber"
                name="callerNumber"
                placeholder="Search by caller number"
                value={filters.callerNumber}
                onChange={handleFilterChange}
                className="pl-3 px-3 py-2 mt-1 block w-full rounded-md bg-gray-50 border border-gray-300 focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
            </div>

            <div className="relative">
              <label htmlFor="targetNumber" className="block text-xs font-medium text-gray-700 mb-1">
                Target Number
              </label>
              <input
                type="text"
                id="targetNumber"
                name="targetNumber"
                placeholder="Search by target number"
                value={filters.targetNumber}
                onChange={handleFilterChange}
                className="pl-3 px-3 py-2 mt-1 block w-full rounded-md bg-gray-50 border border-gray-300 focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
            </div>

            <div className="relative">
              <label htmlFor="startDate" className="block text-xs font-medium text-gray-700 mb-1">
                Start Date
              </label>
              <input
                type="date"
                id="startDate"
                name="startDate"
                value={filters.startDate}
                onChange={handleFilterChange}
                className="pl-3 px-3 py-2 mt-1 block w-full rounded-md bg-gray-50 border border-gray-300 focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
            </div>

            <div className="relative">
              <label htmlFor="endDate" className="block text-xs font-medium text-gray-700 mb-1">
                End Date
              </label>
              <input
                type="date"
                id="endDate"
                name="endDate"
                value={filters.endDate}
                onChange={handleFilterChange}
                className="pl-3 px-3 py-2 mt-1 block w-full rounded-md bg-gray-50 border border-gray-300 focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
            </div>
          </div>

          <div className="flex justify-end mt-4 space-x-2">
            <button
              onClick={resetFilters}
              className="px-3 py-1.5 bg-white text-gray-700 rounded-md border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-300 transition-colors text-sm font-medium"
            >
              Reset
            </button>
            <button
              onClick={applyFilters}
              className="px-3 py-1.5 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-md hover:from-blue-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 text-sm font-medium"
            >
              Apply Filters
            </button>
          </div>
        </div>
      </div>

      {/* Table Section */}
      <div className="mx-auto max-w-full p-4">
        <div className="bg-white">
          <div className="m-4 flow-root">
            <div className="-mx-2 -my-4 overflow-x-auto sm:-mx-6 lg:-mx-8 custom-scrollbar">
              <div className="inline-block min-w-full py-2 align-middle">
                <table className="min-w-full divide-y divide-gray-700">
                  <thead>
                    <tr className="bg-white rounded-lg">
                      {[
                        "Call Date",
                        "DID",
                        "Disposition",
                        "Termination Reason",
                        "Campaign Name",  // Added back Campaign Name
                        "Total Time",
                        "Talk Time",
                        "Ring Time",
                        "Caller Number",
                        "Target Name",
                        "Target Number",
                        "Recording"  // Changed from N/A to actual recording
                      ].map((header, index) => (
                        <th
                          key={header}
                          scope="col"
                          className={`px-3 py-3.5 text-left text-xs font-medium text-black ${
                            index === 0 ? "rounded-tl-lg" : ""
                          } ${index === 11 ? "rounded-tr-lg" : ""}`}
                        >
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-800">
                    {reports.length === 0 ? (
                      <tr>
                        <td colSpan="12" className="text-center py-4 text-xs text-gray-500">
                          No records found
                        </td>
                      </tr>
                    ) : (
                      reports.map((report) => (
                        <tr key={report.id}>
                          <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-900">{report.callDate}</td>
                          <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-900">{report.did}</td>
                          <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-900">{report.disposition}</td>
                          <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-900">{report.terminationReason}</td>
                          <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-900">{report.campaignName}</td>
                          <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-900">{report.totalTime}</td>
                          <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-900">{report.talkTime}</td>
                          <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-900">{report.ringTime}</td>
                          <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-900">{report.callerNumber}</td>
                          <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-900">{report.targetName}</td>
                          <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-900">{report.targetNumber}</td>
                          {/* <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-900">N/A</td> */}
                          <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-900">{report.recording}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between px-2 py-2">
              <div className="flex items-center text-sm text-gray-900">
                <span>Page {page} of {totalPages}</span>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                  disabled={page === 1}
                  className={`px-3 py-1 rounded ${
                    page === 1
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                      : "bg-blue-600 text-white hover:bg-blue-700"
                  }`}
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
                  disabled={page === totalPages}
                  className={`px-3 py-1 rounded ${
                    page === totalPages
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                      : "bg-blue-600 text-white hover:bg-blue-700"
                  }`}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default BuyersReportTable;